import React from "react";
import { Helmet } from "react-helmet-async";
import { MdAccountBalanceWallet } from "react-icons/md";

import styles from "./Bonus.module.scss";
import { useBonus } from "./useBonus";
import { PageHeader } from "../../../common/components/pageHeader/PageHeader";
import { EmptyState } from "../../../common/components/emptyState/EmptyState";
import { StatusChip } from "../../../common/components/statusChip/StatusChip";
import { PROFILE_ROUTE } from "../../../common/utils/constants";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export const Bonus: React.FC = () => {
  const {
    balances,
    selectedBalance,
    transactions,
    clusterId,
    handleSelectCluster,
    ready,
    isFetching,
    loadMore,
    isEmpty,
  } = useBonus();

  if (!ready) return null;

  const hasNoBonusClusters = balances.length === 0;

  return (
    <div className={styles.Page}>
      <Helmet>
        <title>Бонусы — ЛОГМОЛЛ</title>
      </Helmet>

      <PageHeader title="Бонусы" backRoute={PROFILE_ROUTE} />

      {hasNoBonusClusters ? (
        <EmptyState
          icon={MdAccountBalanceWallet}
          title="Бонусы пока недоступны"
          description="Ни в одной из ваших компаний нет бонусной программы."
        />
      ) : (
        <>
          <div className={styles.Filters}>
            {balances.map((b) => (
              <StatusChip
                key={b.clusterId}
                label={b.cluster?.name || `#${b.clusterId}`}
                isActive={clusterId === b.clusterId}
                onClick={() => handleSelectCluster(b.clusterId)}
                compact
              />
            ))}
          </div>

          {selectedBalance && (
            <div className={styles.Hero}>
              <span className={styles.HeroBg} aria-hidden="true">
                {selectedBalance.balance.toLocaleString("ru-RU")} ₽
              </span>
              <div className={styles.HeroContent}>
                <div className={styles.HeroCluster}>
                  {selectedBalance.cluster?.domain && (
                    <img
                      src={`https://${selectedBalance.cluster.domain}/icons/logo512.png`}
                      alt={selectedBalance.cluster.name}
                      className={styles.HeroLogo}
                    />
                  )}
                  <span className={styles.HeroClusterName}>
                    {selectedBalance.cluster?.name ||
                      `Кластер #${selectedBalance.clusterId}`}
                  </span>
                </div>
                <span className={styles.HeroBalance}>
                  {selectedBalance.balance.toLocaleString("ru-RU")} ₽
                </span>
                <span className={styles.HeroPercent}>
                  +{selectedBalance.bonusPercent}%
                </span>
              </div>
            </div>
          )}

          {isEmpty ? (
            <EmptyState
              icon={MdAccountBalanceWallet}
              title="История пуста"
              description="Совершите заказ, чтобы получить бонусы."
            />
          ) : (
            <>
              <div className={styles.List}>
                {transactions.map((t) => (
                  <div key={t.id} className={styles.Row}>
                    <div className={styles.RowInfo}>
                      <span className={styles.RowDesc}>{t.description}</span>
                      <span className={styles.RowDate}>
                        {formatDate(t.createdAt)}
                      </span>
                    </div>
                    <span className={styles.RowAmount} data-type={t.type}>
                      {t.type === 1 ? "+" : "-"}
                      {t.amount.toLocaleString("ru-RU")}
                    </span>
                  </div>
                ))}
              </div>

              {isFetching && <div className={styles.Loader}>Загрузка...</div>}
            </>
          )}
        </>
      )}
    </div>
  );
};
